import { useState, useCallback, useEffect } from 'react';
import { api } from '../lib/api';
import { getStoredToken, getStoredUser, setAuth, clearAuth } from '../lib/auth';
import { useBiometric } from './useBiometric';
import type { User } from '../lib/types';

export function useAuth() {
  const [user, setUser] = useState<User | null>(getStoredUser);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);

  const biometric = useBiometric();
  const isLoggedIn = !!user;

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const { token, user: userData } = await api.login(email, password);
      setAuth(token, userData);

      // If biometric is available but not enabled, show prompt BEFORE navigating
      if (biometric.isAvailable && !biometric.isEnabled) {
        setPendingUser(userData);
        setPendingCredentials({ email, password });
        setShowBiometricPrompt(true);
        // Don't setUser yet — keep on login page to show modal
      } else {
        setUser(userData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [biometric.isAvailable, biometric.isEnabled]);

  const loginWithBiometric = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const creds = await biometric.authenticateWithBiometric();
      if (!creds) {
        setLoading(false);
        return;
      }
      const { token, user: userData } = await api.login(creds.email, creds.password);
      setAuth(token, userData);
      setUser(userData);
    } catch {
      setError('Biometric login failed. Please use your password.');
      await biometric.disableBiometric();
    } finally {
      setLoading(false);
    }
  }, [biometric]);

  // User taps "Enable" on the biometric prompt
  const enableBiometricLogin = useCallback(async () => {
    if (pendingCredentials) {
      await biometric.enableBiometric(pendingCredentials.email, pendingCredentials.password);
    }
    // Now navigate to dashboard
    if (pendingUser) {
      setUser(pendingUser);
    }
    setPendingUser(null);
    setPendingCredentials(null);
    setShowBiometricPrompt(false);
  }, [biometric, pendingCredentials, pendingUser]);

  // User taps "Not Now" on the biometric prompt
  const dismissBiometricPrompt = useCallback(() => {
    // Navigate to dashboard without enabling biometric
    if (pendingUser) {
      setUser(pendingUser);
    }
    setPendingUser(null);
    setPendingCredentials(null);
    setShowBiometricPrompt(false);
  }, [pendingUser]);

  const disableBiometricLogin = useCallback(async () => {
    await biometric.disableBiometric();
  }, [biometric]);

  const logout = useCallback(() => {
    clearAuth();
    setUser(null);
    // Do NOT delete biometric credentials on logout
  }, []);

  // Verify token on mount
  useEffect(() => {
    const token = getStoredToken();
    if (token && !user) {
      api.me().then(({ user }) => setUser(user)).catch(() => clearAuth());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    user, isLoggedIn, loading, error, login, logout,
    loginWithBiometric,
    enableBiometricLogin,
    dismissBiometricPrompt,
    disableBiometricLogin,
    showBiometricPrompt,
    biometricAvailable: biometric.isAvailable,
    biometricEnabled: biometric.isEnabled,
    biometricType: biometric.biometryType,
  };
}
